import React from 'react';
import PropTypes from 'prop-types';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { capitalize } from '@mui/material/utils';

const useStyles = makeStyles()((theme, _params, classes) => ({
  root: {
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: '50%',
    '&.colorDefault': {
      '&': {
        borderColor: theme.palette.primary.main,
      },
      [`& .${classes.icon}, & .${classes.text}`]: {
        color: theme.palette.primary.main,
      },
      '&.completed, &.error, &.active': {
        '&': {
          borderColor: theme.palette.primary.main,
        },
        [`& .${classes.icon}, & .${classes.text}`]: {
          color: theme.palette.primary.main,
        },
      },
    },
    '&.colorWhite': {
      [`& .${classes.icon}`]: {
        width: 16,
        height: 16,
      },
      '&': {
        borderColor: theme.palette.custom.white,
        backgroundColor: 'transparent',
        borderWidth: 1,
      },
      [`& .${classes.icon}, & .${classes.text}`]: {
        color: theme.palette.custom.white,
      },
      '&.active': {
        '&': {
          borderColor: theme.palette.custom.white,
          backgroundColor: theme.palette.custom.white,
        },
        [`& .${classes.icon}, & .${classes.text}`]: {
          color: theme.palette.primary.main,
        },
      },
      '&.disabled': {
        '&': {
          borderColor: theme.palette.custom.white,
        },
        [`& .${classes.icon}, & .${classes.text}`]: {
          color: theme.palette.custom.white,
        },
      },
      '&.completed': {
        '&': {
          borderColor: theme.palette.primary.light,
          borderWidth: 5,
          backgroundColor: theme.palette.custom.white,
        },
        [`& .${classes.icon}, & .${classes.text}`]: {
          color: theme.palette.primary.light,
        },
      },
    },
  },
  text: {
    fontWeight: theme.typography.fontWeightMediumBold,
  },
  icon: {},
  disabled: {},
}));

const getChildren = (active, completed, error, icon, classes) => {
  if (error) return <ErrorRoundedIcon className={classes.icon} />;
  if (completed) return <CheckCircleRoundedIcon className={classes.icon} />;

  return (
    <Typography className={classes.text} variant="caption">
      {icon}
    </Typography>
  );
};

function StepIcon({ active, disabled, completed, error, icon, color = 'default' }) {
  const { classes, cx } = useStyles();
  return (
    <div className={cx(classes.root, `color${capitalize(color)}`, { completed, disabled, active, error })}>
      {getChildren(active, completed, error, icon, classes)}
    </div>
  );
}

StepIcon.muiName = 'StepIcon';

StepIcon.propTypes = {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  completed: PropTypes.bool,
  error: PropTypes.bool,
  icon: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['default', 'white']),
};

export default StepIcon;
